import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IProof } from 'src/ts/interfaces';
import {
  FormValues,
  getProofData,
  isUnverifiedOfferLetter,
  OFFER_LETTER_TYPE,
  PENDING_EMPLOYER_VERIFICATION
} from '../helpers/proofHelpers';

type FormDataUpdater = (
  updater: (prev: Record<string, FormValues>) => Record<string, FormValues>
) => void;

/**
 * Result, reason, OLV reasons and document type for the current proof, saved into
 * `localFormData` the same way the classic DocumentViewer does it.
 */
export const useProofReviewForm = (
  currentProof: IProof | undefined,
  localFormData: Record<string, FormValues>,
  onUpdateLocalFormData: FormDataUpdater | undefined,
  hideResultControls: boolean | undefined
) => {
  const savedData = localFormData[currentProof?.id || ''];
  const { result, reason, documentType, olvReasons } = getProofData(currentProof, savedData);
  const { watch, setValue, getValues } = useForm<FormValues>({
    defaultValues: { result, reason, documentType, olvReasons }
  });

  const watchedResult = watch('result');
  const watchedReason = watch('reason');
  const watchedDocumentType = watch('documentType');
  const watchedOlvReasons = watch('olvReasons');

  const isOlvActionCommitted = useMemo(() => {
    if (!currentProof) return false;
    const formData = localFormData[currentProof.id];
    if (!formData || formData.documentType !== OFFER_LETTER_TYPE) return false;
    return (
      (formData.olvReasons && formData.olvReasons.length > 0) ||
      formData.reason === PENDING_EMPLOYER_VERIFICATION
    );
  }, [currentProof, localFormData]);

  const [initializedProofId, setInitializedProofId] = useState('');
  const latest = useRef({ currentProof, localFormData });
  latest.current = { currentProof, localFormData };
  const proofId = currentProof?.id;

  useEffect(() => {
    if (!currentProof || !onUpdateLocalFormData || hideResultControls) return;
    if (currentProof.id !== initializedProofId) return;
    if (isUnverifiedOfferLetter(currentProof)) return;

    const currentValues = getValues();
    const id = currentProof.id;

    onUpdateLocalFormData((prev) => {
      if (prev[id] && isEqual(prev[id], currentValues)) return prev;
      return { ...prev, [id]: currentValues };
    });
  }, [
    watchedResult,
    watchedReason,
    watchedDocumentType,
    watchedOlvReasons,
    currentProof,
    initializedProofId,
    getValues,
    onUpdateLocalFormData,
    hideResultControls
  ]);

  useEffect(() => {
    if (!currentProof || !onUpdateLocalFormData || !hideResultControls) return;
    if (currentProof.id !== initializedProofId) return;
    if (!watchedDocumentType) return;

    const id = currentProof.id;
    onUpdateLocalFormData((prev) => {
      if (prev[id]?.documentType === watchedDocumentType) return prev;
      return {
        ...prev,
        [id]: { ...(prev[id] || getValues()), documentType: watchedDocumentType }
      };
    });
  }, [
    watchedDocumentType,
    currentProof,
    initializedProofId,
    onUpdateLocalFormData,
    hideResultControls,
    getValues
  ]);

  useEffect(() => {
    const proof = latest.current.currentProof;
    if (!proof) return;
    const saved = latest.current.localFormData[proof.id];
    const initial = getProofData(proof, saved);

    if (proof.is_automatic_review && !saved) {
      setValue('result', initial.result);
      setValue('documentType', initial.documentType);
      setValue('reason', initial.reason || '');
      setValue('olvReasons', initial.olvReasons);
    } else if (saved) {
      setValue('result', saved.result);
      setValue('reason', saved.reason || '');
      setValue('documentType', saved.documentType);
      setValue('olvReasons', saved.olvReasons ?? []);
    } else {
      setValue('result', proof.result || 'PENDING');
      setValue('documentType', proof.type || '');
      if (proof.result === 'EDITED' && proof.result_edited_reason?.length > 0) {
        setValue(
          'reason',
          proof.result_edited_reason.length === 1
            ? proof.result_edited_reason[0]
            : proof.result_edited_reason
        );
      } else if (proof.result === 'UNDETERMINED' && proof.result_insufficient_reason) {
        setValue('reason', proof.result_insufficient_reason);
      } else {
        setValue('reason', '');
      }
      setValue('olvReasons', proof.result_insufficient_reasons ?? []);
    }
    setInitializedProofId(proof.id);
  }, [proofId, setValue]);

  return {
    setValue,
    watchedResult,
    watchedReason,
    watchedDocumentType,
    watchedOlvReasons: watchedOlvReasons ?? [],
    isOlvActionCommitted
  };
};
